import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Wrench, ClipboardCheck, LifeBuoy } from "lucide-react";
import SEO from "@/components/SEO";
import {
  MarketingHeader, MarketingFooter, MarketingBackdrop,
} from "@/components/MarketingShell";

export default function MaintenancePage() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "System maintenance",
    "name": "Solar &amp; electrical system maintenance",
    "provider": { "@id": "https://www.powerbuilt.sx/#org" },
    "areaServed": [
      { "@type": "Country", "name": "Sint Maarten" },
      { "@type": "Country", "name": "Saint Martin" },
    ],
    "description": "Scheduled inspections, cleaning, diagnostics and repairs for solar and electrical systems in Sint Maarten.",
    "url": "https://www.powerbuilt.sx/maintenance",
  };
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home",        "item": "https://www.powerbuilt.sx/" },
      { "@type": "ListItem", "position": 2, "name": "Maintenance", "item": "https://www.powerbuilt.sx/maintenance" },
    ],
  };
  return (
    <MarketingBackdrop>
      <SEO
        path="/maintenance"
        title="Solar &amp; Electrical Maintenance in Sint Maarten — PowerBuilt"
        description="Scheduled inspections, cleaning, diagnostics and repairs to keep your solar and electrical systems generating in Sint Maarten."
        jsonLd={{ "@context": "https://schema.org", "@graph": [jsonLd, breadcrumb] }}
      />
      <MarketingHeader />

      <main className="relative z-10 max-w-6xl mx-auto px-6 pt-8 md:pt-16 pb-16">
        <nav aria-label="Breadcrumb" className="text-xs text-white/50 mb-6">
          <Link to="/" className="hover:text-white">Home</Link> <span className="mx-1">/</span> <span className="text-white/70">Maintenance</span>
        </nav>

        <section className="max-w-3xl">
          <p className="overline text-[#C9A356] mb-4">Service · Maintenance</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-medium tracking-tight leading-[1.05]">
            Keep systems <span className="text-[#B7F58A]">generating</span>.
          </h1>
          <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-lg">
            The Caribbean is hard on hardware. PowerBuilt maintains, inspects and repairs solar and electrical systems so they stay reliable long after handover.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/contact" className="btn-accent text-base px-7 py-3" data-testid="maintenance-cta-quote">
              Request maintenance <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </section>

        <section className="mt-16 border-t border-white/10 pt-12">
          <p className="overline text-[#C9A356] mb-3">What&#39;s included</p>
          <div className="grid md:grid-cols-3 gap-5 mt-4">
            {[
              { icon: ClipboardCheck, title: "Scheduled inspections", copy: "Visual, thermal and electrical checks on modules, inverters, isolators, batteries and grounding — before problems become outages." },
              { icon: Wrench,         title: "Cleaning &amp; repairs", copy: "Panel wash, connector re-torque, filter replacement, faulty component swap — restoring output when we spot drift." },
              { icon: LifeBuoy,       title: "On-call support",       copy: "Existing customers can raise service tickets directly from their PowerBuilt account, with a clear ETA and status." },
            ].map((f) => (
              <article key={f.title} className="group rounded-2xl border border-white/10 bg-white/[0.03] p-7 hover:bg-white/[0.05] transition hover:border-[#C9A356]/50">
                <div className="w-11 h-11 rounded-xl grid place-items-center mb-5 bg-[#C9A356]/15 text-[#C9A356] group-hover:bg-[#C9A356]/25 transition">
                  <f.icon className="w-5 h-5" />
                </div>
                <h2 className="font-heading text-xl mb-2" dangerouslySetInnerHTML={{ __html: f.title }} />
                <p className="text-sm text-slate-400 leading-relaxed" dangerouslySetInnerHTML={{ __html: f.copy }} />
              </article>
            ))}
          </div>
        </section>

        <section className="mt-16 border-t border-white/10 pt-12">
          <p className="overline text-[#C9A356] mb-3">Related</p>
          <div className="flex flex-wrap gap-4">
            <Link to="/solar"      className="px-5 py-2 rounded-md border border-white/20 text-white/85 hover:bg-white/10 transition">Solar &amp; battery storage &rarr;</Link>
            <Link to="/electrical" className="px-5 py-2 rounded-md border border-white/20 text-white/85 hover:bg-white/10 transition">Electrical work &rarr;</Link>
            <Link to="/contact"    className="px-5 py-2 rounded-md border border-white/20 text-white/85 hover:bg-white/10 transition">Contact &rarr;</Link>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </MarketingBackdrop>
  );
}
